import { DatePipe, NgClass } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { UserInfo } from '@shared/models/user-info';
import { SUBSCRIPTION_TAB_PARAM } from 'src/app/account/account.consts';

type SubscriptionBadgeState = 'active' | 'trial' | 'expired' | 'none';
type SubscriptionBadgePlan = 'monthly' | 'quarterly' | 'annual' | 'properties' | 'partner' | 'other';

// Keyed by SubscriptionDetail.period, same mapping as AccBuenoClubComponent.getSubscriptionType().
const PLANS: Record<number, { plan: SubscriptionBadgePlan; label: string }> = {
  0: { plan: 'monthly', label: 'SUBSCRIPTION_PLANS.MONTHLY.TITLE' },
  1: { plan: 'annual', label: 'SUBSCRIPTION_PLANS.ANNUAL.TITLE' },
  2: { plan: 'quarterly', label: 'SUBSCRIPTION_PLANS.QUARTERLY.TITLE' },
  3: { plan: 'properties', label: 'PROPERTY.PROPERTIES' },
};

@Component({
  selector: 'app-subscription-badge',
  templateUrl: './subscription-badge.component.html',
  styleUrl: './subscription-badge.component.scss',
  imports: [NgClass, DatePipe, MatIcon, RouterLink, TranslateModule],
  host: { '[hidden]': '!visible()' },
})
export class SubscriptionBadgeComponent {
  readonly user = input<UserInfo | undefined>();
  readonly subscriptionTabParam = SUBSCRIPTION_TAB_PARAM;

  readonly partner = computed(() => {
    const partnerDetails = this.user()?.partnerDetails;
    return partnerDetails?.isActive ? partnerDetails : null;
  });

  readonly subscription = computed(() => this.user()?.subscriptionsHistory?.find(x => x.isActive));

  // Freshly registered customers have never subscribed, so there's nothing to show them yet.
  readonly visible = computed(() => !!this.partner() || (this.user()?.subscriptionsHistory?.length ?? 0) > 0);

  readonly state = computed<SubscriptionBadgeState>(() => {
    // Partner-paid customers skip the subscription step, so their history is ignored (see OnboardedGuardService).
    if (this.partner()) {
      return 'active';
    }
    const subscription = this.subscription();
    if (!subscription) {
      return 'none';
    }
    if (subscription.isTrialActive) {
      return 'trial';
    }
    return new Date(subscription.paidUntil) > new Date() ? 'active' : 'expired';
  });

  readonly plan = computed<SubscriptionBadgePlan>(() => {
    if (this.partner()) {
      return 'partner';
    }
    const subscription = this.subscription();
    return (subscription && PLANS[subscription.period]?.plan) || 'other';
  });

  readonly planLabel = computed(() => {
    // Partner customers show the partner's name in the template; this is only the fallback when it's empty.
    if (this.partner()) {
      return 'PROPERTY.BUENO_CLUB';
    }
    const subscription = this.subscription();
    if (!subscription) {
      return 'BUENO_CLUB.SUBSCRIPTION_STATUS.NO_SUBSCRIPTION';
    }
    return PLANS[subscription.period]?.label ?? 'PROPERTY.BUENO_CLUB';
  });

  readonly statusLabel = computed(() => {
    switch (this.state()) {
      case 'trial':
        return 'BUENO_CLUB.SUBSCRIPTION_STATUS.TRIAL_ACTIVE';
      case 'expired':
        return 'BUENO_CLUB.SUBSCRIPTION_STATUS.EXPIRED';
      default:
        return null;
    }
  });
}
